import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HorizontalBarChart } from '../../components/dashboard/HorizontalBarChart';
import { StatCard } from '../../components/dashboard/StatCard';
import { StatCardTone } from '../../components/dashboard/StatCardTone';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { CodedStatusBadge } from '../../components/ui/CodedStatusBadge';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import {
  AreaDashboard,
  AreaDashboardKey,
  fetchAreaDashboard,
} from '../../services/areaDashboardApi';

const STAGE_LABELS: Record<string, string> = {
  Lead: 'Lead',
  Qualified: 'Qualificado',
  Proposal: 'Proposta',
  Negotiation: 'Negociação',
  Won: 'Ganho',
  Lost: 'Perdido',
};

const ENRICHMENT_LABELS: Record<string, string> = {
  Pending: 'Pendente',
  InProgress: 'Em progresso',
  Completed: 'Concluído',
  Failed: 'Falhou',
  Skipped: 'Ignorado',
};

const TICKET_STATUS_LABELS: Record<string, string> = {
  New: 'Novo',
  Triaged: 'Triado',
  InProgress: 'Em andamento',
  PendingCustomer: 'Aguardando cliente',
  Resolved: 'Resolvido',
  Closed: 'Fechado',
  Reopened: 'Reaberto',
};

const TASK_STATUS_LABELS: Record<string, string> = {
  Planned: 'Planejada',
  InProgress: 'Em andamento',
  Completed: 'Concluída',
  Cancelled: 'Cancelada',
  Overdue: 'Atrasada',
  Pending: 'Pendente',
  Active: 'Ativo',
  Inactive: 'Inativo',
  ...ENRICHMENT_LABELS,
  ...TICKET_STATUS_LABELS,
};

const AREA_CHART_CONFIG: Record<
  AreaDashboardKey,
  { title: string; labels: Record<string, string>; format: 'currency' | 'number' }
> = {
  commercial: { title: 'Pipeline por estágio', labels: STAGE_LABELS, format: 'currency' },
  operations: { title: 'Tarefas por status', labels: TASK_STATUS_LABELS, format: 'number' },
  marketing: { title: 'Prospects por campanha', labels: {}, format: 'number' },
  people: { title: 'Colaboradores por perfil', labels: {}, format: 'number' },
  'customer-success': { title: 'Chamados por status', labels: TICKET_STATUS_LABELS, format: 'number' },
  relationship: { title: 'Clientes por situação', labels: {}, format: 'number' },
};

const FOCUS_SECTION_TITLES: Record<AreaDashboardKey, string> = {
  commercial: 'Tarefas em foco',
  operations: 'Tarefas em foco',
  marketing: 'Prospects em foco',
  people: 'Colaboradores em foco',
  'customer-success': 'Chamados em foco',
  relationship: 'Clientes em foco',
};

const KPI_TONES: StatCardTone[] = [
  'primary',
  'sky',
  'emerald',
  'violet',
  'amber',
  'teal',
];

type AreaDashboardPageProps = {
  area: AreaDashboardKey;
  title: string;
  description: string;
};

function formatDueDate(value?: string): string {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleDateString('pt-BR');
}

function mapChartLabels(items: AreaDashboard['charts'], labels: Record<string, string>) {
  return items.map((item) => ({
    ...item,
    label: labels[item.label] ?? item.label,
  }));
}

export function AreaDashboardPage({ area, title, description }: AreaDashboardPageProps) {
  const [dashboard, setDashboard] = useState<AreaDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(false);
      try {
        setDashboard(await fetchAreaDashboard(area));
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [area]);

  if (loading) {
    return <LoadingSpinner label="Carregando visão geral..." />;
  }

  if (error || !dashboard) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">
        Não foi possível carregar a visão geral. Tente novamente em instantes.
      </div>
    );
  }

  const chartConfig = AREA_CHART_CONFIG[area];
  const chartItems = mapChartLabels(dashboard.charts, chartConfig.labels);
  const valueFormatter =
    chartConfig.format === 'currency'
      ? (value: number) =>
          value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
      : (value: number) => value.toLocaleString('pt-BR');

  return (
    <PageShell variant="dashboard">
      <PageHeader
        title={title}
        description={description}
        actions={
          <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-700">
            {dashboard.scopeLabel}
          </span>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {dashboard.kpis.map((kpi, index) => (
          <StatCard
            key={kpi.code}
            label={kpi.label}
            value={kpi.value}
            hint={kpi.hint}
            tone={KPI_TONES[index % KPI_TONES.length]}
          />
        ))}
      </section>

      <section className="mt-6">
        <HorizontalBarChart
          title={chartConfig.title}
          items={chartItems}
          valueFormatter={valueFormatter}
        />
      </section>

      <section className="mt-6 rounded-xl border border-secondary-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <h3 className="font-semibold text-secondary-900">{FOCUS_SECTION_TITLES[area]}</h3>
          <span className="text-sm text-secondary-500">{dashboard.tasks.length} item(ns)</span>
        </div>

        {dashboard.tasks.length === 0 ? (
          <p className="mt-4 text-sm text-secondary-500">Nenhum item pendente no seu escopo.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-secondary-200 text-secondary-600">
                <tr>
                  <th className="px-2 py-2 font-medium">Item</th>
                  <th className="px-2 py-2 font-medium">Responsável</th>
                  <th className="px-2 py-2 font-medium">Status</th>
                  <th className="px-2 py-2 font-medium">Prazo</th>
                  <th className="px-2 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {dashboard.tasks.map((task) => (
                  <tr key={task.id} className="border-b border-secondary-100 last:border-0">
                    <td className="px-2 py-3">
                      <p className="font-medium text-secondary-900">{task.title}</p>
                      {task.subtitle ? (
                        <p className="text-xs text-secondary-500">{task.subtitle}</p>
                      ) : null}
                    </td>
                    <td className="px-2 py-3 text-secondary-700">{task.assigneeName ?? '—'}</td>
                    <td className="px-2 py-3">
                      <CodedStatusBadge
                        status={task.isOverdue ? 'Overdue' : task.status}
                        label={TASK_STATUS_LABELS[task.status] ?? task.status}
                      />
                    </td>
                    <td className="px-2 py-3 text-secondary-700">{formatDueDate(task.dueAtUtc)}</td>
                    <td className="px-2 py-3 text-right">
                      <Link
                        to={task.deepLink}
                        className="text-sm font-medium text-primary-600 hover:text-primary-700"
                      >
                        Abrir
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </PageShell>
  );
}
